import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { SegmentEvent } from "src/utils/segment/types/SegmentBaseTypes";
import { AnalyticsBrowser } from "@segment/analytics-next";
import { addDatadogError } from "src/utils/datadog";
import { initializeOneTrustTracking } from "./oneTrustTracking";
import { getSqreemEventProperties } from "./sqreemTracking";

type SegmentContextValues = {
  track?: (event: SegmentEvent) => void;
  /** Canonical Segment page() for destinations like Pardot that require it for web visit tracking. Returns Promise so callers can await and catch async rejections. */
  page?: (name: string, properties?: Record<string, string>) => Promise<void>;
};

const SegmentContext = createContext<SegmentContextValues>({});

export default SegmentContext;

export const SegmentProvider = ({
  children,
  analytics,
}: {
  children: ReactNode;
  analytics: AnalyticsBrowser;
}) => {
  const track = useCallback(
    async (event: SegmentEvent) => {
      // Skip tracking if DISABLE_SEGMENT_TRACKING is set to "true"
      if (process.env.GATSBY_DISABLE_SEGMENT_TRACKING === "true") {
        return;
      }

      try {
        const properties = {
          ...getSqreemEventProperties(),
          ...event.properties,
          platform: "aws",
        };
        await analytics.track(event.eventName, properties);
      } catch (e) {
        // Log error to Datadog for monitoring but don't throw
        addDatadogError("SegmentContextError", String(e));
        // Don't re-throw - tracking failures should not break the app
      }
    },
    [analytics],
  );

  const page = useCallback(
    async (name: string, properties?: Record<string, string>) => {
      if (process.env.GATSBY_DISABLE_SEGMENT_TRACKING === "true") {
        return;
      }

      try {
        await analytics.page(name, properties ?? {});
      } catch (e) {
        addDatadogError("SegmentContextPageError", String(e));
        // Don't re-throw — single reporting site so only this key fires for analytics.page() failures; monitor SegmentContextPageError.
      }
    },
    [analytics],
  );

  const contextValues = useMemo(() => {
    return {
      track,
      page,
    };
  }, [track, page]);

  return (
    <SegmentContext.Provider value={contextValues}>
      {children}
    </SegmentContext.Provider>
  );
};

// Singleton analytics instance — created once at module load so it can be
// imported directly by utilities (e.g. anonymousIdTracking) without needing
// React context. Segment populates analytics.instance after load() resolves.
export const analytics = new AnalyticsBrowser();

// Function to wrap a ReactNode with a SegmentProvider based on provided options. Intended to be called within the Gatsby config APIs.
export const wrapWithSegment = (writeKey: string) => {
  return ({ element }: { element: ReactElement }) => {
    // Load asynchronously while still wrapping the root or page element.
    const deferredLoad = async () => {
      try {
        await analytics.load({ writeKey: writeKey });
        // Initialize OneTrust tracking after Segment is loaded
        initializeOneTrustTracking(analytics);

        // The SDK uses tld() to find the highest domain it can write a cookie
        // to (e.g. .hingehealth.dev). If tld() previously failed (sandboxed
        // context, privacy settings), a cookie was written to the exact host
        // (www.hingehealth.dev) instead. js-cookie reads the more-specific
        // cookie first, so MWEB and the AQ would load different anonymousIds —
        // breaking Segment Unify cross-domain stitching.
        // Fix: purge any exact-host cookie, then re-sync the SDK to whichever
        // root-domain cookie survives.
        const { hostname } = window.location;
        document.cookie = `ajs_anonymous_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${hostname}`;
        document.cookie = `ajs_anonymous_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${hostname}`;
        const rawCookie = document.cookie
          .split(";")
          .map(c => c.trim())
          .find(c => c.startsWith("ajs_anonymous_id="))
          ?.substring("ajs_anonymous_id=".length);
        const canonicalId =
          rawCookie ?? analytics.instance?.user().anonymousId();
        if (canonicalId) {
          analytics.instance?.setAnonymousId(canonicalId);
        }
      } catch (e) {
        addDatadogError("SegmentContextInitError", String(e));
      }
    };
    void deferredLoad();

    return <SegmentProvider analytics={analytics}>{element}</SegmentProvider>;
  };
};
